import type {
  LatLng,
  NpcStation,
  StationAddress,
  StationKind,
  CityAddressType,
} from '@/state/types';
import { haversineKm } from '@/lib/geo';
import { getCurrentTier } from './stations';
import {
  classifyPoliceStation,
  POLICE_ACQUIRE_DISCOUNT,
  POLICE_MIN_REVIER,
  POLICE_MIN_FEDERAL,
} from './policeStations';
import { cityHasBerufsfeuerwehr } from './germanBfCities';

/**
 * Phase 7.2.4 — NPC station discovery (OSM Overpass + synthetic fallback).
 *
 * Strategy mirrors hydrants:
 *   1. Try Overpass for `amenity=fire_station` and `amenity=hospital`.
 *   2. Cache per-city in localStorage.
 *   3. Synthetic fallback: distribute 1–3 stations per 10 km² in the radius.
 *
 * Acquire cost = 70 % of tier-1 build cost (player picks up an existing
 * structure, no construction time).
 */

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const ACQUIRE_DISCOUNT = 0.7;

function cacheKey(center: LatLng, radiusKm: number): string {
  return `npc_stations_${center.lat.toFixed(3)}_${center.lng.toFixed(3)}_${radiusKm}`;
}

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  /** `out center` returns this for ways/relations. */
  center?: { lat: number; lon: number };
  tags?: {
    name?: string;
    amenity?: string;
    building?: string;
    'addr:street'?: string;
    'addr:housenumber'?: string;
    'addr:postcode'?: string;
    'addr:city'?: string;
    [key: string]: string | undefined;
  };
}
interface OverpassResponse {
  elements: OverpassElement[];
}

/**
 * Phase 11.2.1 (Bug N1) — Hybrid heuristic to classify a fire station as
 * BF or FF.
 *
 * Order of evidence:
 *   1. Explicit FF marker in the name → ff-station.
 *   2. Explicit BF marker in the name → bf-station.
 *   3. Numbered "Feuerwache N" (1..20) inside a city with a known BF → bf-station.
 *   4. City is a Berufsfeuerwehr-Stadt (whitelist or address-type "city") and
 *      population ≥ 100 000 → bf-station.
 *   5. Otherwise → ff-station.
 */
export function classifyFireStation(
  name: string | undefined,
  cityAddressType?: CityAddressType,
  cityName?: string,
  cityPopulation?: number,
): 'bf-station' | 'ff-station' {
  const n = (name ?? '').toLowerCase();

  // Explicit FF wins to avoid false positives from generic city heuristics.
  if (
    /\bff\b/.test(n) ||
    /\bffw\b/.test(n) ||
    n.includes('freiwillig')
  ) {
    return 'ff-station';
  }

  // Explicit BF markers.
  if (
    n.includes('berufsfeuerwehr') ||
    /\bbf\b/.test(n) ||
    n.includes('hauptwache') ||
    n.includes('feuer- und rettungswache') ||
    n.includes('feuer und rettungswache')
  ) {
    return 'bf-station';
  }

  const cityIsBf = cityHasBerufsfeuerwehr(cityName);
  // "Feuerwache 7" / "Feuerwehrwache 12" inside a BF city → BF.
  const numberedRegex = /\bfeuer(wehr)?wache\s*\d+\b/;
  if (cityIsBf && numberedRegex.test(n)) return 'bf-station';

  // Population-based fallback: cities >300k almost certainly have BF.
  if (cityPopulation && cityPopulation >= 300_000) return 'bf-station';

  // Whitelist match without a name signal → still BF (rare unmarked stations).
  if (cityIsBf && cityAddressType === 'city') return 'bf-station';

  // Address-type fallback for larger towns.
  if (cityAddressType === 'city' && (cityPopulation ?? 0) >= 100_000) return 'bf-station';

  return 'ff-station';
}

export async function fetchNpcStationsFromOverpass(
  center: LatLng,
  radiusKm: number,
  signal?: AbortSignal,
  ctx?: { cityName?: string; cityPopulation?: number; cityAddressType?: CityAddressType },
): Promise<NpcStation[] | null> {
  // Phase 8.1.6 — wider buffer (+50 % radius), include `building=fire_station`
  // ways/relations as well as nodes for better coverage.
  const radiusM = Math.round(radiusKm * 1.5 * 1000);
  const query = `[out:json][timeout:25];
(
  node["amenity"="fire_station"](around:${radiusM},${center.lat},${center.lng});
  way["amenity"="fire_station"](around:${radiusM},${center.lat},${center.lng});
  way["building"="fire_station"](around:${radiusM},${center.lat},${center.lng});
  node["amenity"="hospital"](around:${radiusM},${center.lat},${center.lng});
  way["amenity"="hospital"](around:${radiusM},${center.lat},${center.lng});
  node["amenity"="police"](around:${radiusM},${center.lat},${center.lng});
  way["amenity"="police"](around:${radiusM},${center.lat},${center.lng});
);
out center body;`;
  try {
    const resp = await fetch(OVERPASS_URL, {
      method: 'POST',
      body: 'data=' + encodeURIComponent(query),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      signal,
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as OverpassResponse;
    if (!Array.isArray(data.elements)) return null;
    return data.elements
      .map((n) => convertOverpassElement(n, ctx))
      .filter((s): s is NpcStation => s !== null);
  } catch {
    return null;
  }
}

function convertOverpassElement(
  el: OverpassElement,
  ctx?: { cityName?: string; cityPopulation?: number; cityAddressType?: CityAddressType },
): NpcStation | null {
  const amenity = el.tags?.amenity;
  const building = el.tags?.building;
  const fallbackName =
    amenity === 'hospital'
      ? 'Krankenhaus'
      : amenity === 'police'
        ? 'Polizeirevier'
        : 'Feuerwache';
  const name = el.tags?.name ?? fallbackName;
  let kind: StationKind;
  if (amenity === 'hospital') {
    kind = 'hospital';
  } else if (amenity === 'fire_station' || building === 'fire_station') {
    kind = classifyFireStation(
      el.tags?.name,
      ctx?.cityAddressType,
      ctx?.cityName,
      ctx?.cityPopulation,
    );
  } else if (amenity === 'police') {
    kind = classifyPoliceStation(el.tags?.name, ctx?.cityAddressType);
  } else {
    return null;
  }
  // Resolve coordinates: nodes have lat/lon directly, ways have `center`.
  const lat = el.lat ?? el.center?.lat;
  const lon = el.lon ?? el.center?.lon;
  if (lat === undefined || lon === undefined) return null;
  const tier1Cost = getCurrentTier(kind, 1).cost;
  // Phase 9.6.3 — Echte Adresse aus OSM-Tags.
  const address: StationAddress | undefined =
    el.tags?.['addr:street'] || el.tags?.['addr:city']
      ? {
          street: el.tags['addr:street'],
          houseNumber: el.tags['addr:housenumber'],
          postcode: el.tags['addr:postcode'],
          city: el.tags['addr:city'],
        }
      : undefined;
  // courthouse cannot be acquired; the converter never emits it.
  if (kind === 'courthouse') return null;
  return {
    id: `osm_npc_${el.type}_${el.id}`,
    name,
    kind,
    position: { lat, lng: lon },
    source: 'osm',
    acquireCost: Math.round(tier1Cost * ACQUIRE_DISCOUNT),
    address,
  };
}

/** Phase 8.1.6 — Minimum NPC counts per kind to ensure healthy gameplay. */
export const NPC_MIN_FF = 8;
export const NPC_MIN_BF = 2;
export const NPC_MIN_HOSPITALS = 3;

/**
 * Synthetic fallback. Distributes ~1 station per 4 km² + 1 hospital per 25 km².
 * Used when Overpass is unavailable or returns nothing.
 */
export function generateFallbackNpcStations(
  center: LatLng,
  radiusKm: number,
  cityAddressType?: CityAddressType,
  rng: () => number = Math.random,
): NpcStation[] {
  const out: NpcStation[] = [];
  const areaKm2 = Math.PI * radiusKm * radiusKm;
  // Phase 8.1.6 — denser spawn (1 per 4 km² instead of 10), 1 KH per 25 km².
  const fireCount = Math.max(NPC_MIN_FF + NPC_MIN_BF, Math.round(areaKm2 / 4));
  const hospCount = Math.max(NPC_MIN_HOSPITALS, Math.round(areaKm2 / 25));

  const cosLat = Math.cos((center.lat * Math.PI) / 180);

  function randomPoint(): LatLng {
    const r = radiusKm * Math.sqrt(rng());
    const t = rng() * Math.PI * 2;
    const dLat = (r * Math.cos(t)) / 111.32;
    const dLng = (r * Math.sin(t)) / (111.32 * cosLat);
    return { lat: center.lat + dLat, lng: center.lng + dLng };
  }

  for (let i = 0; i < fireCount; i++) {
    const pos = randomPoint();
    const kind = cityAddressType === 'city' && rng() < 0.4 ? 'bf-station' : 'ff-station';
    out.push({
      id: `syn_npc_fire_${i}`,
      name: `${kind === 'bf-station' ? 'BF' : 'FF'} Standort ${i + 1}`,
      kind,
      position: pos,
      source: 'synthetic',
      acquireCost: Math.round(getCurrentTier(kind, 1).cost * ACQUIRE_DISCOUNT),
    });
  }
  for (let i = 0; i < hospCount; i++) {
    const pos = randomPoint();
    out.push({
      id: `syn_npc_hosp_${i}`,
      name: `Klinikum Standort ${i + 1}`,
      kind: 'hospital',
      position: pos,
      source: 'synthetic',
      acquireCost: Math.round(getCurrentTier('hospital', 1).cost * ACQUIRE_DISCOUNT),
    });
  }
  // Phase 10.1.4 — Police minimums.
  for (let i = 0; i < POLICE_MIN_REVIER; i++) {
    const pos = randomPoint();
    out.push({
      id: `syn_npc_pol_${i}`,
      name: `Polizeirevier Standort ${i + 1}`,
      kind: 'police-station',
      position: pos,
      source: 'synthetic',
      acquireCost: Math.round(
        getCurrentTier('police-station', 1).cost * POLICE_ACQUIRE_DISCOUNT,
      ),
    });
  }
  for (let i = 0; i < POLICE_MIN_FEDERAL; i++) {
    const pos = randomPoint();
    out.push({
      id: `syn_npc_bpol_${i}`,
      name: `Bundespolizei Standort ${i + 1}`,
      kind: 'federal-police-station',
      position: pos,
      source: 'synthetic',
      acquireCost: Math.round(
        getCurrentTier('federal-police-station', 1).cost * POLICE_ACQUIRE_DISCOUNT,
      ),
    });
  }
  return out;
}

/**
 * Top-level loader (mirrors hydrants.loadHydrants).
 */
export async function loadNpcStations(
  center: LatLng,
  radiusKm: number,
  cityAddressType?: CityAddressType,
  signal?: AbortSignal,
  ctx?: { cityName?: string; cityPopulation?: number },
): Promise<NpcStation[]> {
  const key = cacheKey(center, radiusKm);
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as NpcStation[];
        if (Array.isArray(parsed)) {
          // Phase 13.1 — defensiv auch beim Cache-Read deduplizieren, weil
          // ältere Caches (vor Phase 13) Duplikate enthalten können.
          return dedupeNpcStations(parsed);
        }
      }
    } catch {
      // bad cache — ignore
    }
  }

  const live = await fetchNpcStationsFromOverpass(center, radiusKm, signal, {
    cityName: ctx?.cityName,
    cityPopulation: ctx?.cityPopulation,
    cityAddressType,
  });

  // Phase 8.1.6 — top up to the minimum coverage (8 FF + 2 BF + 3 KH).
  // Phase 10.1.4 — additionally ensure 5 Polizeireviere + 1 BPOL.
  const merged: NpcStation[] = live ? [...live] : [];
  const counts = (() => {
    let ff = 0;
    let bf = 0;
    let hosp = 0;
    let pol = 0;
    let bpol = 0;
    for (const s of merged) {
      if (s.kind === 'ff-station') ff++;
      else if (s.kind === 'bf-station') bf++;
      else if (s.kind === 'hospital') hosp++;
      else if (s.kind === 'police-station') pol++;
      else if (s.kind === 'federal-police-station') bpol++;
    }
    return { ff, bf, hosp, pol, bpol };
  })();

  const needFf = Math.max(0, NPC_MIN_FF - counts.ff);
  const needBf = Math.max(0, NPC_MIN_BF - counts.bf);
  const needHosp = Math.max(0, NPC_MIN_HOSPITALS - counts.hosp);
  const needPol = Math.max(0, POLICE_MIN_REVIER - counts.pol);
  const needBpol = Math.max(0, POLICE_MIN_FEDERAL - counts.bpol);

  if (needFf + needBf + needHosp + needPol + needBpol > 0) {
    const synth = generateFallbackNpcStations(center, radiusKm, cityAddressType);
    let addedFf = 0;
    let addedBf = 0;
    let addedHosp = 0;
    let addedPol = 0;
    let addedBpol = 0;
    for (const s of synth) {
      if (s.kind === 'ff-station' && addedFf < needFf) {
        merged.push(s);
        addedFf++;
      } else if (s.kind === 'bf-station' && addedBf < needBf) {
        merged.push(s);
        addedBf++;
      } else if (s.kind === 'hospital' && addedHosp < needHosp) {
        merged.push(s);
        addedHosp++;
      } else if (s.kind === 'police-station' && addedPol < needPol) {
        merged.push(s);
        addedPol++;
      } else if (s.kind === 'federal-police-station' && addedBpol < needBpol) {
        merged.push(s);
        addedBpol++;
      }
      if (
        addedFf >= needFf &&
        addedBf >= needBf &&
        addedHosp >= needHosp &&
        addedPol >= needPol &&
        addedBpol >= needBpol
      )
        break;
    }
  }

  // Phase 13.1 — Dedupe by (kind + rounded lat/lng). Overpass occasionally
  // returns the same station as both a node and a way (gleicher Ort, andere
  // OSM-IDs); zusätzlich sammeln sich beim Hot-Reload manchmal Doppel-Einträge
  // im Cache. Wir behalten den ersten Treffer pro Schlüssel.
  const deduped = dedupeNpcStations(merged);

  persist(key, deduped);
  return deduped;
}

/**
 * Phase 13.1 — Same-spot dedupe.
 *
 * Two NPC stations are "the same place" when they share kind + lat/lng
 * rounded to ~10 m precision (4 decimal places). The first occurrence wins.
 */
export function dedupeNpcStations(stations: NpcStation[]): NpcStation[] {
  const seen = new Set<string>();
  const out: NpcStation[] = [];
  for (const s of stations) {
    const key = `${s.kind}_${s.position.lat.toFixed(4)}_${s.position.lng.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function persist(key: string, items: NpcStation[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // ignore
  }
}

/** Filter NPC list by minimum distance to existing player stations. */
export function filterAvailableNpcs(
  npcs: NpcStation[],
  ownedPositions: LatLng[],
  minDistanceKm = 0.5,
): NpcStation[] {
  return npcs.filter((n) => {
    return ownedPositions.every((p) => haversineKm(p, n.position) >= minDistanceKm);
  });
}
